/**
 * Recall Check — master-admin configuration panel.
 *
 * Mounts as a tab on the dealer detail page. Master admin only.
 * This is where the à la carte product is switched on and off, and
 * where the light per-dealer branding lives.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  Loader2, Save, ShieldCheck, ShieldOff, Copy, ExternalLink, AlertTriangle,
} from "lucide-react";
import { DEFAULT_ACCENT, DEFAULT_BRAND, contrastWarning, isHex, readableFg } from "./theme";
import RecallMismatchWatchlist from "./RecallMismatchWatchlist";
import LogoUploadField from "../components/admin/LogoUploadField";

const INPUT_CLS =
  "w-full rounded-md border border-gray-200 px-3 py-2 text-[13px] text-gray-900 outline-none focus:border-gray-900 focus:ring-0";

/** 555-01xx is the reserved fictional range — never a real service line. */
const isPlaceholderPhone = (v) => /5\s?5\s?5[\s.-]?01\d\d/.test(String(v || ""));

const slugify = (s) =>
  String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

export default function RecallCheckAdminPanel({ dealer }) {
  const { apiFetch } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/admin/dealers/${dealer.id}/recall/settings`, { method: "GET" });
      setDraft(r?.data || null);
    } catch {
      toast?.({ title: "Failed to load Recall Check settings", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [dealer.id, apiFetch, toast]);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async (override = {}) => {
    if (saving) return;
    const next = { ...draft, ...override };
    if (next.recall_brand_color && !isHex(next.recall_brand_color)) {
      toast?.({ title: "Brand color must be a hex value like #1c3f5e", variant: "destructive" });
      return;
    }
    if (next.recall_accent_color && !isHex(next.recall_accent_color)) {
      toast?.({ title: "Accent color must be a hex value like #e8a13a", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const r = await apiFetch(`/admin/dealers/${dealer.id}/recall/settings`, {
        method: "PATCH",
        data: {
          recall_check_enabled: !!next.recall_check_enabled,
          recall_service_phone: next.recall_service_phone || "",
          recall_brand_color: next.recall_brand_color || "",
          recall_accent_color: next.recall_accent_color || "",
          recall_logo_url: next.recall_logo_url || "",
          recall_allowed_domains: next.recall_allowed_domains || "",
          recall_popup_style: next.recall_popup_style || "floating_tab",
          slug: next.slug || "",
        },
      });
      setDraft(r?.data || next);
      toast?.({ title: "Recall Check settings saved." });
    } catch (e) {
      toast?.({ title: String(e?.response?.data?.detail || "Save failed"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 flex items-center gap-2 text-gray-500 text-[13px]">
        <Loader2 className="animate-spin" size={16} /> Loading Recall Check…
      </div>
    );
  }

  const ref = draft.slug || draft.public_key;
  const pageUrl = `${window.location.origin}/recall/${draft.slug || ""}`;
  const snippet = `<script src="${window.location.origin}/api/recall-widget.js" data-dealer="${ref || ""}" data-mode="popup"></script>`;
  const brandWarn = contrastWarning(draft.recall_brand_color);
  const accentWarn = contrastWarning(draft.recall_accent_color);
  const on = !!draft.recall_check_enabled;

  const copy = (txt, label) => {
    navigator.clipboard?.writeText(txt);
    toast?.({ title: `${label} copied.` });
  };

  return (
    <div className="space-y-4" data-testid="recall-admin-panel">
      {/* Entitlement */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
              {on ? <ShieldCheck size={16} className="text-emerald-600" /> : <ShieldOff size={16} className="text-gray-400" />}
              Recall Check — à la carte product
            </h3>
            <p className="text-[12px] text-gray-500 mt-1 max-w-2xl">
              One switch governs all three surfaces: the embeddable widget, the hosted landing
              page, and the batch VIN upload tool. When off, the widget renders nothing on the
              dealer&apos;s live site, the page returns &quot;not available&quot;, and no leads are captured.
            </p>
          </div>
          <button
            type="button"
            onClick={() => save({ recall_check_enabled: !on })}
            disabled={saving}
            data-testid="recall-entitlement-toggle"
            className={`shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
              on ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {saving ? "Saving…" : on ? "Enabled — click to turn off" : "Disabled — click to turn on"}
          </button>
        </div>
        <div
          className={`mt-3 text-[12px] rounded-md px-3 py-2 ${on ? "bg-emerald-50 text-emerald-800" : "bg-gray-50 text-gray-600"}`}
          data-testid="recall-entitlement-state"
        >
          {on
            ? "Live for this dealer. Billing should drive this switch — turn it off when they stop paying."
            : "Not sold to this dealer. All three surfaces are rejected server-side."}
        </div>
      </div>

      {/* Popup display style */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-3"
           data-testid="recall-popup-style-card">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900">
            Popup display style
          </h3>
          <p className="text-[12px] text-gray-500 mt-1 max-w-2xl">
            How the popup surface appears on the dealer&apos;s site. Only affects
            the popup embed (<code>data-mode=&quot;popup&quot;</code>). Inline mode and the
            hosted landing page are unchanged.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <StyleOption
            active={draft.recall_popup_style !== "auto_popup"}
            onClick={() => set({ recall_popup_style: "floating_tab" })}
            testId="recall-popup-style-floating-tab"
            title="Floating tab"
            subtitle="Persistent corner button — visitor clicks to open"
            badge="Default"
          >
            <FloatingTabGlyph />
          </StyleOption>
          <StyleOption
            active={draft.recall_popup_style === "auto_popup"}
            onClick={() => set({ recall_popup_style: "auto_popup" })}
            testId="recall-popup-style-auto-popup"
            title="Auto popup"
            subtitle="No corner tab — modal opens on dwell or exit-intent (like Test Drive)"
          >
            <AutoPopupGlyph />
          </StyleOption>
        </div>
        <div className="text-[11.5px] text-gray-500 leading-relaxed">
          Auto popup still obeys popup coordination — priority order and page
          rules decide whether it fires here or another popup wins.
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        <h3 className="text-[15px] font-semibold text-gray-900">Dealer configuration</h3>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Service department phone" hint="Shown as tap-to-call when we can't confirm a result.">
            <input
              className={INPUT_CLS} data-testid="recall-service-phone"
              value={draft.recall_service_phone || ""}
              placeholder={draft.dealer_phone || "(813) 555-0142"}
              onChange={(e) => set({ recall_service_phone: e.target.value })}
            />
            {isPlaceholderPhone(draft.recall_service_phone) && (
              <span className="block text-[11.5px] text-amber-700 mt-1 flex items-start gap-1" data-testid="recall-phone-warning">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                That&apos;s a 555 placeholder number. Swap in the real service line before this dealer
                goes live — customers tap it from the amber and error states.
              </span>
            )}
          </Field>

          <Field label="Landing page slug" hint="Used for the hosted page URL. Lowercase letters, numbers, hyphens.">
            <div className="flex gap-2">
              <input
                className={INPUT_CLS} data-testid="recall-slug"
                value={draft.slug || ""}
                placeholder={slugify(draft.dealer_name)}
                onChange={(e) => set({ slug: slugify(e.target.value) })}
              />
              <button
                type="button"
                className="text-[12px] px-3 rounded-md border border-gray-200 hover:bg-gray-50"
                onClick={() => set({ slug: slugify(draft.dealer_name) })}
              >
                Suggest
              </button>
            </div>
          </Field>

          <ColorField
            label="Brand color" testId="recall-brand-color"
            value={draft.recall_brand_color || DEFAULT_BRAND}
            onChange={(v) => set({ recall_brand_color: v })}
            warning={brandWarn}
          />
          <ColorField
            label="Accent color" testId="recall-accent-color"
            value={draft.recall_accent_color || DEFAULT_ACCENT}
            onChange={(v) => set({ recall_accent_color: v })}
            warning={accentWarn}
          />

          <Field label="Logo" hint="Upload PNG / JPG / WebP / SVG (auto-scaled to ≤ 200 KB) — or paste a hosted URL. Leave blank to use the dealer initials.">
            <LogoUploadField
              value={draft.recall_logo_url || ""}
              onChange={(v) => set({ recall_logo_url: v || "" })}
              testId="recall-logo-upload"
            />
          </Field>

          <Field label="Allowed widget domains" hint="Comma-separated. Required — a blank list blocks the widget on every external site.">
            <input
              className={INPUT_CLS} data-testid="recall-allowed-domains"
              value={draft.recall_allowed_domains || ""}
              placeholder="baymotors.com, www.baymotors.com"
              onChange={(e) => set({ recall_allowed_domains: e.target.value })}
            />
            {!((draft.recall_allowed_domains || "").trim()) && (
              <span className="block text-[11.5px] text-amber-700 mt-1 flex items-start gap-1" data-testid="recall-allowlist-warning">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                No domains listed — the widget will be refused on the dealer&apos;s site. Add their
                domain(s) before sending the embed snippet.
              </span>
            )}
          </Field>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button" onClick={() => save()} disabled={saving}
            data-testid="recall-save-settings"
            className="inline-flex items-center gap-2 rounded-md bg-gray-900 text-white text-[13px] font-semibold px-4 py-2 hover:bg-gray-800 disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save configuration
          </button>
          <span className="text-[11.5px] text-gray-400">
            State colors (green / red / amber) are fixed and never themeable — they carry safety meaning.
          </span>
        </div>
      </div>

      {/* Preview + install */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        <h3 className="text-[15px] font-semibold text-gray-900">Install</h3>

        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Embed snippet (dealer&apos;s site)
          </div>
          <div className="flex gap-2">
            <code
              className="flex-1 text-[11.5px] bg-gray-900 text-gray-100 rounded-md px-3 py-2.5 overflow-x-auto whitespace-nowrap"
              data-testid="recall-embed-snippet"
            >
              {snippet}
            </code>
            <button
              type="button" onClick={() => copy(snippet, "Snippet")}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 text-[12px] hover:bg-gray-50"
              data-testid="recall-copy-snippet"
            >
              <Copy size={13} /> Copy
            </button>
          </div>
          <p className="text-[11.5px] text-gray-500 mt-1.5">
            Use <code>data-mode=&quot;inline&quot;</code> plus <code>data-target=&quot;#selector&quot;</code> to drop it into a page section instead of a floating button.
          </p>
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Hosted landing page
          </div>
          {draft.slug ? (
            <a
              href={pageUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] text-blue-700 hover:underline"
              data-testid="recall-page-link"
            >
              {pageUrl} <ExternalLink size={13} />
            </a>
          ) : (
            <p className="text-[12.5px] text-amber-700 flex items-center gap-1.5">
              <AlertTriangle size={14} /> Set a slug above to activate the hosted page.
            </p>
          )}
        </div>

        <RecallPreview
          brand={draft.recall_brand_color || DEFAULT_BRAND}
          accent={draft.recall_accent_color || DEFAULT_ACCENT}
          name={draft.dealer_name}
        />
      </div>

      <RecallMismatchWatchlist />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[11.5px] text-gray-400 mt-1">{hint}</span>}
    </label>
  );
}

function ColorField({ label, value, onChange, warning, testId }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</span>
      <div className="flex gap-2 items-center">
        <input
          type="color" value={isHex(value) ? value : "#1c3f5e"}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded border border-gray-200 bg-white p-0.5 cursor-pointer"
          data-testid={`${testId}-picker`}
        />
        <input
          className={`${INPUT_CLS} font-mono`} value={value} data-testid={testId}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {warning && (
        <span className="block text-[11.5px] text-amber-700 mt-1 flex items-start gap-1" data-testid={`${testId}-warning`}>
          <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {warning}
        </span>
      )}
    </label>
  );
}

/** Static chrome preview — shows exactly which elements the two tokens drive. */
function RecallPreview({ brand, accent, name }) {
  const fg = readableFg(brand);
  const accentFg = readableFg(accent);
  return (
    <div className="max-w-sm rounded-xl overflow-hidden border border-gray-200" data-testid="recall-chrome-preview">
      <div className="bg-gray-50 border-b border-gray-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
        Preview — illustration only, not a live lookup
      </div>
      <div style={{ background: brand, color: fg }} className="px-4 pt-3.5 pb-3 relative">
        <div className="flex items-center gap-2">
          <span style={{ background: accent, color: accentFg }} className="w-7 h-7 rounded-md grid place-items-center text-[11px] font-extrabold">
            {(name || "VD").slice(0, 2).toUpperCase()}
          </span>
          <span className="text-[9.5px] tracking-[0.18em] uppercase font-bold opacity-80">Free safety check</span>
        </div>
        <div className="text-[16px] font-extrabold mt-2 leading-tight">Check your vehicle for open recalls</div>
        <div style={{ background: accent }} className="absolute left-0 right-0 bottom-0 h-1" />
      </div>
      <div className="p-4 bg-white space-y-2">
        <div className="border-2 border-gray-200 rounded-lg bg-gray-50 p-1 flex items-center">
          <span className="flex-1 px-2 py-2 font-mono text-[13px] tracking-widest text-gray-400">ENTER 17-CHAR VIN</span>
          <span style={{ background: brand, color: fg }} className="rounded-md text-[12px] font-bold px-3 py-2">Check VIN</span>
        </div>
        <div className="border border-gray-200 rounded-lg p-3" style={{ borderLeft: "5px solid #1e6b4a" }}>
          <div className="text-[13px] font-extrabold" style={{ color: "#1e6b4a" }}>No open recalls</div>
          <div className="text-[11px] text-gray-500 font-mono">example clear result</div>
        </div>
        <div style={{ background: accent, color: accentFg }} className="rounded-full text-center text-[12.5px] font-extrabold py-2.5">
          Request my free recall repair
        </div>
      </div>
    </div>
  );
}


function StyleOption({ active, onClick, testId, title, subtitle, badge, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-lg border p-4 flex gap-4 items-start transition-colors ${
        active
          ? "border-gray-900 bg-gray-50 ring-1 ring-gray-900"
          : "border-gray-200 hover:border-gray-400 bg-white"
      }`}
    >
      <div className="w-16 h-16 rounded-md border border-gray-200 bg-white shrink-0 grid place-items-center overflow-hidden">
        {children}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-semibold text-gray-900">{title}</span>
          {badge && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-100 border border-gray-200 px-1.5 rounded">
              {badge}
            </span>
          )}
          {active && (
            <span className="ml-auto text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700"
                  data-testid={`${testId}-active`}>
              Selected
            </span>
          )}
        </div>
        <div className="text-[11.5px] text-gray-500 mt-1 leading-snug">{subtitle}</div>
      </div>
    </button>
  );
}

/** Illustrative glyph: page with a corner tab in the bottom-right. */
function FloatingTabGlyph() {
  return (
    <svg viewBox="0 0 64 64" width="46" height="46" aria-hidden>
      <rect x="6" y="8" width="52" height="48" rx="4" fill="#f3f4f6" stroke="#9ca3af" strokeWidth="1.5" />
      <rect x="10" y="14" width="30" height="2.5" rx="1.25" fill="#cbd5e1" />
      <rect x="10" y="20" width="22" height="2.5" rx="1.25" fill="#e5e7eb" />
      <rect x="10" y="26" width="26" height="2.5" rx="1.25" fill="#e5e7eb" />
      <rect x="10" y="32" width="20" height="2.5" rx="1.25" fill="#e5e7eb" />
      <rect x="38" y="46" width="18" height="8" rx="4" fill="#1c3f5e" />
      <circle cx="42" cy="50" r="1.4" fill="#e8a13a" />
    </svg>
  );
}

/** Illustrative glyph: modal centered over a dimmed page. */
function AutoPopupGlyph() {
  return (
    <svg viewBox="0 0 64 64" width="46" height="46" aria-hidden>
      <rect x="6" y="8" width="52" height="48" rx="4" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="1.5" />
      <rect x="18" y="18" width="28" height="28" rx="3" fill="#ffffff" stroke="#1c3f5e" strokeWidth="1.6" />
      <rect x="22" y="24" width="14" height="2" rx="1" fill="#1c3f5e" />
      <rect x="22" y="30" width="20" height="2" rx="1" fill="#cbd5e1" />
      <rect x="22" y="38" width="16" height="4" rx="2" fill="#e8a13a" />
    </svg>
  );
}
